import { useState } from 'react';
import { useTranslation } from 'react-i18next';

// TODO replace after BSC testnet/mainnet deploy
const CONTRACT_ADDRESS = '0x0000000000000000000000000000000000000000';
const MULTISIG_ADDRESS = '0x0000000000000000000000000000000000000000';
const IS_DEPLOYED = CONTRACT_ADDRESS !== '0x0000000000000000000000000000000000000000';

const shortAddr = (addr) => `${addr.slice(0, 6)}…${addr.slice(-4)}`;

const CopyButton = ({ value, label }) => {
  const [status, setStatus] = useState('idle'); // 'idle' | 'ok' | 'err'

  const handleCopy = async () => {
    try {
      if (!navigator.clipboard) throw new Error('no-clipboard');
      await navigator.clipboard.writeText(value);
      setStatus('ok');
    } catch {
      setStatus('err');
    }
    setTimeout(() => setStatus('idle'), 1400);
  };

  const icon = status === 'ok' ? 'fa-check' : status === 'err' ? 'fa-triangle-exclamation' : 'fa-copy';

  return (
    <button type="button" className="sec-copy" onClick={handleCopy} aria-label={label}>
      <i className={`fas ${icon}`} />
    </button>
  );
};

const SecuritySection = () => {
  const { t } = useTranslation();

  return (
    <section id="security" className="section-x security">
      <div className="gold-orb gold-orb--deep security__orb-a" />
      <div className="gold-orb gold-orb--emerald security__orb-b" />

      <div className="container">
        <div className="row">
          <div className="col-lg-9 mx-auto text-center mb-5">
            <div className="eyebrow eyebrow--center mb-3" data-aos="fade-up">
              <span>{t('security.eyebrow')}</span>
            </div>
            <h2 className="section-heading" data-aos="fade-up">
              {t('security.titleLead')}{' '}
              <span className="gold-text">{t('security.titleHighlight')}</span>
            </h2>
            <p className="section-subheading" data-aos="fade-up" data-aos-delay="80">
              {t('security.subtitle')}
            </p>
          </div>
        </div>

        <div className="row g-4">
          {/* Contract address card */}
          <div className="col-lg-6" data-aos="fade-up">
            <div className="sec-card sec-card--primary">
              <div className="sec-card__label">
                <i className="fas fa-file-contract" />
                <span>{t('security.contract.label')}</span>
                <span className={`sf-badge ${IS_DEPLOYED ? 'sf-badge--emerald' : 'sf-badge--outline'}`}>
                  {IS_DEPLOYED ? t('security.verified') : t('security.pending')}
                </span>
              </div>
              <div className="sec-card__value mono">
                <span className="sec-card__addr-full">{CONTRACT_ADDRESS}</span>
                <span className="sec-card__addr-short">{shortAddr(CONTRACT_ADDRESS)}</span>
                <CopyButton value={CONTRACT_ADDRESS} label="Copy contract address" />
              </div>
              <div className="sec-card__actions">
                <a
                  className="sec-link"
                  href={`https://bscscan.com/address/${CONTRACT_ADDRESS}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-up-right-from-square" />
                  {t('security.viewBscscan')}
                </a>
                <a
                  className="sec-link"
                  href={`https://bscscan.com/address/${CONTRACT_ADDRESS}#code`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <i className="fas fa-code" />
                  {t('security.readCode')}
                </a>
              </div>
            </div>
          </div>

          {/* Multisig card */}
          <div className="col-lg-6" data-aos="fade-up" data-aos-delay="80">
            <div className="sec-card">
              <div className="sec-card__label">
                <i className="fas fa-user-shield" />
                <span>{t('security.multisig.label')}</span>
                <span className="sf-badge sf-badge--outline">3/5</span>
              </div>
              <div className="sec-card__value mono">
                <span className="sec-card__addr-full">{MULTISIG_ADDRESS}</span>
                <span className="sec-card__addr-short">{shortAddr(MULTISIG_ADDRESS)}</span>
                <CopyButton value={MULTISIG_ADDRESS} label="Copy multisig address" />
              </div>
              <p className="sec-card__note">{t('security.multisig.note')}</p>
            </div>
          </div>

          {/* Immutability */}
          <div className="col-md-4" data-aos="fade-up">
            <div className="sec-card sec-card--compact">
              <div className="sec-card__icon">
                <i className="fas fa-lock" />
              </div>
              <h4 className="sec-card__title">{t('security.immutable.title')}</h4>
              <p className="sec-card__desc">{t('security.immutable.description')}</p>
            </div>
          </div>

          {/* Audit */}
          <div className="col-md-4" data-aos="fade-up" data-aos-delay="80">
            <div className="sec-card sec-card--compact">
              <div className="sec-card__icon">
                <i className="fas fa-shield-halved" />
              </div>
              <h4 className="sec-card__title">{t('security.audit.title')}</h4>
              <p className="sec-card__desc">{t('security.audit.description')}</p>
              <a className="sec-link" href="/audit.pdf" target="_blank" rel="noopener noreferrer">
                <i className="fas fa-file-pdf" />
                {t('security.audit.cta')}
              </a>
            </div>
          </div>

          {/* Open source */}
          <div className="col-md-4" data-aos="fade-up" data-aos-delay="160">
            <div className="sec-card sec-card--compact">
              <div className="sec-card__icon">
                <i className="fab fa-github" />
              </div>
              <h4 className="sec-card__title">{t('security.openSource.title')}</h4>
              <p className="sec-card__desc">{t('security.openSource.description')}</p>
              <a
                className="sec-link"
                href="https://github.com/scrooge-finance"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-up-right-from-square" />
                {t('security.openSource.cta')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SecuritySection;
